import React, { useState, useCallback, useRef } from 'react';
import { useAuth } from '../../lib/auth/AuthContext';
import { useApi } from '../../lib/apiClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Button,
    Card,
    CardContent,
    Input,
    Label,
    Skeleton,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Checkbox,
} from '@pagespeedify/ui';
import {
    ImageIcon,
    Upload,
    Github,
    FolderOpen,
    X,
    Sparkles,
    Shield,
    CheckCircle2,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

interface RepoTree {
    totalImages: number;
    directories: { path: string; imageCount: number; images: string[] }[];
    allImages: { path: string; size: number }[];
    branches: string[];
    currentBranch: string;
    defaultBranch: string;
}

// Simple badge component
const SimpleBadge = ({ className, children }: { className?: string; children: React.ReactNode }) => (
    <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full ${className}`}>
        {children}
    </span>
);

const UsageEstimate = ({ count, usage }: { count: number; usage: any }) => {
    if (!usage || count === 0) return null;

    const remainingInBundle = usage.remainingInBundle || 0;
    const overageCostPerUnit = usage.overageCostPerUnit || 0;
    const userCredits = usage.billingUserCredits || 0;

    const inBundle = Math.min(count, remainingInBundle);
    const overage = Math.max(0, count - remainingInBundle);
    const estimatedCost = overage * overageCostPerUnit;
    const hasSufficientCredits = userCredits >= estimatedCost;

    let headingText = '';
    if (overage === 0) {
        headingText = 'Within plan limits';
    } else {
        headingText = hasSufficientCredits ? 'Sufficient credits' : 'Insufficient credits';
    }

    return (
        <div className={`mt-4 rounded-lg p-3 text-sm border ${hasSufficientCredits ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
            <div className="flex items-center gap-2 font-medium mb-1">
                {hasSufficientCredits ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                    <AlertCircle className="h-4 w-4 text-red-500" />
                )}
                <span className={hasSufficientCredits ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                    {headingText}
                </span>
            </div>

            <div className="space-y-1 pl-6 text-muted-foreground">
                <p>
                    <span className="font-semibold text-foreground">{count}</span> image{count !== 1 ? 's' : ''}:{' '}
                    {inBundle > 0 && (
                        <span>{inBundle} Included in plan</span>
                    )}
                    {inBundle > 0 && overage > 0 && ' + '}
                    {overage > 0 && (
                        <span className="text-amber-600 dark:text-amber-500">
                            {overage} credits (overage)
                        </span>
                    )}
                    {(inBundle === 0 && overage === 0) && '0 Included'}
                </p>
                {overage > 0 && (
                    <p>
                        Total Cost: <span className="font-semibold text-foreground">{estimatedCost.toLocaleString()}</span> credits
                    </p>
                )}
                <p className="text-xs pt-1 border-t mt-1 border-border/50">
                    Your balance: <span className="font-medium">{userCredits.toLocaleString()}</span> credits
                </p>
            </div>
        </div>
    );
};

interface ImageOptimizationFormProps {
    onSuccess?: (taskId: string) => void;
    onCancel?: () => void;
}

export const ImageOptimizationForm: React.FC<ImageOptimizationFormProps> = ({ onSuccess, onCancel }) => {
    const { } = useAuth();
    const api = useApi();
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Upload state
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [optimizationType, setOptimizationType] = useState<'lossy' | 'lossless'>('lossy');
    const [outputFormat, setOutputFormat] = useState<'webp' | 'avif' | 'png' | 'jpeg'>('webp');
    const [taskTitle, setTaskTitle] = useState('');

    // GitHub state
    const [selectedRepo, setSelectedRepo] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [selectedPaths, setSelectedPaths] = useState<string[]>([]);
    const [repoSearch] = useState('');
    const [isLinking, setIsLinking] = useState(false);
    const [imagePreviewPage, setImagePreviewPage] = useState(0);
    const IMAGES_PER_PAGE = 20;

    // Fetch connected repos
    const { data: connectedRepos, isLoading: isLoadingRepos } = useQuery<string[]>({
        queryKey: ['githubRepos'],
        queryFn: async () => {
            const response = await api.get<any>('/github/repos');
            const repos = response.data || [];
            // Normalize: API may return objects ({ full_name, ... }) or plain strings
            return repos.map((r: any) =>
                typeof r === 'string' ? r : (r.full_name || r.name || String(r))
            ) as string[];
        },
        staleTime: 1000 * 60 * 5,
    });



    // Fetch branches for selected repo


    // Reset branch when repo changes
    React.useEffect(() => {
        if (selectedRepo) {
            setSelectedBranch('');
        }
    }, [selectedRepo]);


    // Fetch repository tree
    const { data: repoTree, isLoading: isLoadingTree } = useQuery<RepoTree>({
        queryKey: ['repoTree', selectedRepo, selectedBranch],
        queryFn: async () => {
            const [owner, repo] = selectedRepo.split('/');
            // Consolidated endpoint handles both tree and branches
            const url = selectedBranch
                ? `/github/repos/${owner}/${repo}/tree?branch=${selectedBranch}`
                : `/github/repos/${owner}/${repo}/tree`;

            const response = await api.get<{ data: RepoTree }>(url);
            return response.data;
        },
        enabled: !!selectedRepo,
        staleTime: 1000 * 60 * 5,
    });

    // Fetch usage for cost estimation
    const { data: usageData } = useQuery({
        queryKey: ['imageOptimizationUsage'],
        queryFn: async () => {
            const response = await api.get<any>('/images/usage');
            return response.usage;
        },
    });

    const canAfford = (count: number) => {
        if (!usageData) return true; // Fail open if loading? or closed? Let's assume true for now or block. But actually safe default is true until loaded.
        // Better:
        const remainingInBundle = usageData.remainingInBundle || 0;
        const overageCostPerUnit = usageData.overageCostPerUnit || 0;
        const userCredits = usageData.billingUserCredits || 0;
        const overage = Math.max(0, count - remainingInBundle);
        const estimatedCost = overage * overageCostPerUnit;
        return userCredits >= estimatedCost;
    };

    // Upload mutation
    const uploadMutation = useMutation({
        mutationFn: async (files: File[]) => {
            const formData = new FormData();
            files.forEach((file) => {
                formData.append('files', file);
            });
            formData.append('optimization_type', optimizationType);
            formData.append('format', outputFormat);
            if (taskTitle) formData.append('title', taskTitle);

            const response = await api.post('/images/upload', formData);
            return response;
        },
        onSuccess: (data) => {
            toast.success(`${selectedFiles.length} image(s) queued for optimization`);
            setSelectedFiles([]);
            queryClient.invalidateQueries({ queryKey: ['imageOptimizationUsage'] });
            queryClient.invalidateQueries({ queryKey: ['image-optimization-tasks'] });
            queryClient.invalidateQueries({ queryKey: ['image-tasks-nav'] });
            queryClient.invalidateQueries({ queryKey: ['ai-tasks-list'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            onSuccess?.(data.task_id);
        },
        onError: (error: any) => {
            toast.error('Upload failed', {
                description: error.response?.data?.message || error.message,
            });
        },
    });

    // GitHub mutation
    const githubMutation = useMutation({
        mutationFn: async (data: { repoUrl: string; branch: string; pathFilter?: string }) => {
            const response = await api.post('/images/github', {
                repo_url: data.repoUrl,
                branch: data.branch,
                path_filter: data.pathFilter || undefined,
                optimization_type: optimizationType,
                format: outputFormat,
                title: taskTitle || undefined,
            });
            return response;
        },
        onSuccess: (data) => {
            toast.success('Repository queued for optimization');
            queryClient.invalidateQueries({ queryKey: ['imageOptimizationUsage'] });
            queryClient.invalidateQueries({ queryKey: ['image-optimization-tasks'] });
            queryClient.invalidateQueries({ queryKey: ['image-tasks-nav'] });
            queryClient.invalidateQueries({ queryKey: ['ai-tasks-list'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            onSuccess?.(data.task_id);
        },
        onError: (error: any) => {
            console.error('GitHub mutation error:', error);
            const errData = error.response?.data;
            const code = errData?.error;
            const status = error.response?.status;

            if (code === 'GITHUB_AUTH_ERROR' || code === 'GITHUB_SCOPE_ERROR') {
                toast.error('GitHub connection issue', {
                    description: errData.message,
                    action: {
                        label: 'Relink GitHub',
                        onClick: () => handleLinkGitHub()
                    },
                    duration: 10000,
                });
            } else if (error.code === 'ECONNABORTED' || status === 504) {
                // It's a timeout, likely the repo is large but processing started.
                toast.warning('Request timed out, but optimization may have started.', {
                    description: 'Please check your task list in a few moments.',
                    duration: 5000,
                });
                // We could try to close the form, but we don't have task ID.
            } else {
                toast.error('GitHub optimization failed', {
                    description: errData?.message || error.message || 'Unknown error occurred',
                });
            }
        },
    });

    // File handlers
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        setSelectedFiles(prev => [...prev, ...imageFiles]);
    };

    const removeFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = () => {
        if (selectedFiles.length === 0) {
            toast.error('Please select files to upload');
            return;
        }
        uploadMutation.mutate(selectedFiles);
    };

    const handleLinkGitHub = async () => {
        setIsLinking(true);
        try {
            // Use the same API-based approach as CreateGitHubIssueModal
            // Pass source so the callback redirects back to image-optimization page
            const response = await api.post<{ redirectUrl?: string; isAlreadyLinked?: boolean }>('/github/auth/start', {
                source: 'image-optimization'
            });

            if (response?.redirectUrl) {
                // Redirect to GitHub OAuth
                window.location.href = response.redirectUrl;
            } else if (response?.isAlreadyLinked) {
                toast.success('Your GitHub account is already linked!');
                // Refresh repos query
                queryClient.invalidateQueries({ queryKey: ['githubRepos'] });
            } else {
                toast.error('Could not get GitHub authentication URL.');
            }
        } catch (error) {
            console.error('[GitHub] Error in handleLinkGitHub:', error);
            toast.error('Failed to start GitHub connection');
        } finally {
            setIsLinking(false);
        }
    };

    const handleGitHubOptimize = () => {
        if (!selectedRepo) {
            toast.error('Please select a repository');
            return;
        }

        const finalRepoUrl = `https://github.com/${selectedRepo}`;
        const finalPathFilter = selectedPaths.length === 0 ? '' : selectedPaths.join(',');

        githubMutation.mutate({
            repoUrl: finalRepoUrl,
            branch: selectedBranch || repoTree?.currentBranch || 'main',
            pathFilter: finalPathFilter
        });
    };

    // Drag handlers
    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        setSelectedFiles(prev => [...prev, ...imageFiles]);
    }, []);

    const filteredRepos = connectedRepos?.filter(
        repo => typeof repo === 'string' && repo.toLowerCase().includes(repoSearch.toLowerCase())
    ) || [];

    const isProcessing = uploadMutation.isPending || githubMutation.isPending;

    return (
        <div className="space-y-4">
            <div className="grid gap-2">
                <Label htmlFor="title">Task Name (Optional)</Label>
                <Input
                    id="title"
                    placeholder="e.g. Hero Images Redesign"
                    value={taskTitle}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTaskTitle(e.target.value)}
                />
            </div>

            <Tabs defaultValue="github" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="github" className="flex items-center gap-2">
                        <Github className="h-4 w-4" />
                        GitHub
                    </TabsTrigger>
                    <TabsTrigger value="upload" className="flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Upload
                    </TabsTrigger>
                </TabsList>

                {/* Optimization settings (shared) */}
                <div className="mt-4 flex flex-wrap items-center gap-4">
                    {/* Optimization Type - compact toggle */}
                    <div className="flex items-center gap-2">
                        <Label className="text-sm whitespace-nowrap">Type:</Label>
                        <div className="inline-flex rounded-md border border-input overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setOptimizationType('lossy')}
                                className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors ${optimizationType === 'lossy'
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-background hover:bg-muted'
                                    }`}
                            >
                                <Sparkles className="h-3.5 w-3.5" />
                                Lossy
                            </button>
                            <button
                                type="button"
                                onClick={() => setOptimizationType('lossless')}
                                className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors border-l ${optimizationType === 'lossless'
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-background hover:bg-muted'
                                    }`}
                            >
                                <Shield className="h-3.5 w-3.5" />
                                Lossless
                            </button>
                        </div>
                    </div>

                    {/* Output Format - inline */}
                    <div className="flex items-center gap-2">
                        <Label htmlFor="output-format" className="text-sm whitespace-nowrap">Format:</Label>
                        <Select value={outputFormat} onValueChange={(v: any) => setOutputFormat(v)}>
                            <SelectTrigger id="output-format" className="w-[160px] h-8">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="webp">WebP (Recommended)</SelectItem>
                                <SelectItem value="avif">AVIF (Best compression)</SelectItem>
                                <SelectItem value="png">PNG</SelectItem>
                                <SelectItem value="jpeg">JPEG</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* GitHub Tab */}
                <TabsContent value="github" className="space-y-4 mt-4">
                    {/* Repository selector */}
                    <div className="space-y-2">
                        <Label>Repository</Label>
                        {isLoadingRepos ? (
                            <Skeleton className="h-10 w-full" />
                        ) : connectedRepos && connectedRepos.length > 0 ? (
                            <Select value={selectedRepo} onValueChange={setSelectedRepo}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a repository" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px]">
                                    {filteredRepos.map(repo => (
                                        <SelectItem key={repo} value={repo}>
                                            <div className="flex items-center gap-2">
                                                <Github className="h-4 w-4" />
                                                {repo}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full"
                                onClick={handleLinkGitHub}
                                disabled={isLinking}
                            >
                                {isLinking ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                    <Github className="h-4 w-4 mr-2" />
                                )}
                                Connect GitHub
                            </Button>
                        )}
                    </div>

                    {/* Branch selector */}
                    {selectedRepo && (
                        <div className="space-y-2">
                            <Label>Branch</Label>
                            {!repoTree && isLoadingTree ? (
                                <Skeleton className="h-10 w-full" />
                            ) : (
                                <Select
                                    value={selectedBranch || repoTree?.currentBranch || ''}
                                    onValueChange={setSelectedBranch}
                                    disabled={!repoTree}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a branch" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[300px]">
                                        {repoTree?.branches?.map(branch => (
                                            <SelectItem key={branch} value={branch}>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-muted-foreground">⑂</span>
                                                    {branch}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                    )}

                    {/* Directory picker */}
                    {selectedRepo && (
                        <div className="space-y-3">
                            <Label className="flex justify-between">
                                <span>Select Directories</span>
                                {repoTree && (
                                    <span className="text-muted-foreground font-normal text-xs">
                                        {repoTree.totalImages} images total
                                    </span>
                                )}
                            </Label>

                            {isLoadingTree ? (
                                <Skeleton className="h-24 w-full" />
                            ) : repoTree ? (
                                <div className="space-y-3">
                                    <Card className="bg-card border">
                                        <CardContent className="p-3 space-y-1 max-h-[200px] overflow-y-auto">
                                            <label className={`flex items-center gap-3 p-2 rounded-md cursor-pointer border transition-colors ${selectedPaths.length === 0 ? 'bg-primary/10 border-primary' : 'border-transparent hover:bg-muted/50'}`}>
                                                <Checkbox
                                                    checked={selectedPaths.length === 0}
                                                    onCheckedChange={(checked) => {
                                                        if (checked) {
                                                            setSelectedPaths([]);
                                                            setImagePreviewPage(0);
                                                        }
                                                    }}
                                                    className="border-2"
                                                />
                                                <span className="text-sm font-medium flex-1">📁 Entire Repository</span>
                                                <SimpleBadge className="bg-primary/20 text-primary">
                                                    {repoTree.totalImages}
                                                </SimpleBadge>
                                            </label>
                                            <div className="border-t my-2" />
                                            {repoTree.directories.filter(d => d.path !== '' && d.imageCount > 0).map(dir => (
                                                <label key={dir.path} className={`flex items-center gap-3 p-2 rounded-md cursor-pointer border transition-colors ${selectedPaths.includes(dir.path) ? 'bg-primary/10 border-primary' : 'border-transparent hover:bg-muted/50'}`}>
                                                    <Checkbox
                                                        checked={selectedPaths.includes(dir.path)}
                                                        onCheckedChange={(checked) => {
                                                            if (checked) {
                                                                setSelectedPaths(prev => [...prev, dir.path]);
                                                            } else {
                                                                setSelectedPaths(prev => prev.filter(p => p !== dir.path));
                                                            }
                                                            setImagePreviewPage(0);
                                                        }}
                                                        className="border-2"
                                                    />
                                                    <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                                                    <span className="text-sm flex-1 truncate">{dir.path}</span>
                                                    <SimpleBadge className="bg-muted text-foreground">
                                                        {dir.imageCount}
                                                    </SimpleBadge>
                                                </label>
                                            ))}
                                        </CardContent>
                                    </Card>

                                    {selectedPaths.length > 0 && (
                                        <p className="text-xs text-muted-foreground">
                                            {selectedPaths.length} director{selectedPaths.length === 1 ? 'y' : 'ies'} selected ({
                                                repoTree.directories
                                                    .filter(d => selectedPaths.includes(d.path))
                                                    .reduce((sum, d) => sum + d.imageCount, 0)
                                            } images)
                                        </p>
                                    )}

                                    {/* Image preview with pagination */}
                                    <Card className="bg-muted/30 border-dashed">
                                        <CardContent className="p-3">
                                            {(() => {
                                                const imagesToShow = selectedPaths.length === 0
                                                    ? repoTree.allImages
                                                    : repoTree.allImages.filter(img =>
                                                        selectedPaths.some(p => {
                                                            if (p === '/') return !img.path.includes('/');
                                                            return img.path.startsWith(p + '/') || img.path.split('/').slice(0, -1).join('/') === p;
                                                        })
                                                    );

                                                const totalPages = Math.ceil(imagesToShow.length / IMAGES_PER_PAGE);
                                                const paginatedImages = imagesToShow.slice(
                                                    imagePreviewPage * IMAGES_PER_PAGE,
                                                    (imagePreviewPage + 1) * IMAGES_PER_PAGE
                                                );

                                                return (
                                                    <>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <p className="text-xs font-medium">
                                                                {imagesToShow.length} images to optimize
                                                            </p>
                                                            {totalPages > 1 && (
                                                                <div className="flex items-center gap-1">
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-6 w-6 p-0"
                                                                        onClick={() => setImagePreviewPage(p => Math.max(0, p - 1))}
                                                                        disabled={imagePreviewPage === 0}
                                                                    >
                                                                        <ChevronLeft className="h-4 w-4" />
                                                                    </Button>
                                                                    <span className="text-xs text-muted-foreground min-w-[60px] text-center">
                                                                        {imagePreviewPage + 1} / {totalPages}
                                                                    </span>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-6 w-6 p-0"
                                                                        onClick={() => setImagePreviewPage(p => Math.min(totalPages - 1, p + 1))}
                                                                        disabled={imagePreviewPage >= totalPages - 1}
                                                                    >
                                                                        <ChevronRight className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-1">
                                                            {paginatedImages.map((img, i) => (
                                                                <span key={i} className="text-xs text-muted-foreground truncate flex items-center gap-1">
                                                                    <ImageIcon className="h-2.5 w-2.5 shrink-0" />
                                                                    {img.path}
                                                                </span>
                                                            ))}
                                                        </div>
                                                        {imagesToShow.length === 0 && (
                                                            <p className="text-xs text-muted-foreground">No images found in selected directories.</p>
                                                        )}
                                                        {/* Usage Estimate for GitHub */}
                                                        <UsageEstimate count={imagesToShow.length} usage={usageData} />
                                                    </>
                                                );
                                            })()}
                                        </CardContent>
                                    </Card>
                                </div>
                            ) : (
                                <p className="text-sm text-red-500">Failed to load repository contents.</p>
                            )}
                        </div>
                    )}

                    <Button
                        type="button"
                        onClick={handleGitHubOptimize}
                        disabled={!selectedRepo || isProcessing || !canAfford(
                            // Calculate count again or assume repoTree is enough if selectedPaths is empty?
                            // Need to be careful here. We filtered `imagesToShow` inside the render block.
                            // Better to memoize it or recalculate.
                            // For safety, let's recalculate simply:
                            (() => {
                                if (!repoTree) return 0;
                                if (selectedPaths.length === 0) return repoTree.totalImages;
                                return repoTree.directories
                                    .filter(d => selectedPaths.includes(d.path))
                                    .reduce((sum, d) => sum + d.imageCount, 0);
                            })()
                        )}
                        className="w-full"
                    >
                        {githubMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                            <Github className="h-4 w-4 mr-2" />
                        )}
                        Optimize & Create PR
                    </Button>
                </TabsContent>

                {/* Upload Tab */}
                <TabsContent value="upload" className="space-y-4 mt-4">
                    <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
                            }`}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleFileSelect}
                            className="hidden"
                        />
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground mb-2">
                            Drag and drop images here, or
                        </p>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Browse Files
                        </Button>
                    </div>

                    {selectedFiles.length > 0 && (
                        <div className="space-y-2">
                            <Label>Selected Files ({selectedFiles.length})</Label>
                            <div className="max-h-[150px] overflow-y-auto space-y-1">
                                {selectedFiles.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded text-sm">
                                        <span className="truncate flex-1">{file.name}</span>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0"
                                            onClick={() => removeFile(index)}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Usage Estimate for Upload */}
                    <UsageEstimate count={selectedFiles.length} usage={usageData} />


                    <Button
                        type="button"
                        onClick={handleUpload}
                        disabled={selectedFiles.length === 0 || isProcessing || !canAfford(selectedFiles.length)}
                        className="w-full"
                    >
                        {uploadMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                            <Upload className="h-4 w-4 mr-2" />
                        )}
                        Upload & Optimize
                    </Button>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default ImageOptimizationForm;
